import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    goal: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: 'ease-in-out',
      once: true
    });
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: "Thanks for reaching out. I'll get back to you within 24 hours.",
      });

      setFormData({
        name: '',
        email: '',
        phone: '',
        goal: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try calling or emailing directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="display-large" data-aos="fade-up">Get In Touch</h1>
          <p className="body-large" data-aos="fade-up" data-aos-delay="100">
            Ready to transform your fitness? Let's start with a free consultation.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-wrapper" data-aos="fade-right">
              <h2 className="heading-2">Book Your Free Consultation</h2>
              <p className="body-medium mb-6">
                Fill out the form below and I'll get back to you within 24 hours to schedule your consultation.
              </p>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="778-123-4567"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="goal" className="form-label">Primary Fitness Goal *</label>
                  <Input
                    id="goal"
                    name="goal"
                    type="text"
                    value={formData.goal}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Weight Loss, Muscle Gain, Marathon Training"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Tell Me About Yourself</label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Share your current fitness level, any injuries or limitations, and what you hope to achieve..."
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2" size={18} />
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-wrapper" data-aos="fade-left">
              <h2 className="heading-2">Contact Information</h2>
              <p className="body-medium mb-6">
                Prefer to reach out directly? Here's how you can contact me:
              </p>

              <div className="contact-info-cards">
                <div className="contact-info-card">
                  <Phone size={32} className="contact-info-icon" />
                  <h3 className="heading-3">Phone</h3>
                  <a href="tel:7787886448" className="body-medium contact-link">
                    778-788-6448
                  </a>
                  <p className="caption">Call or text anytime</p>
                </div>

                <div className="contact-info-card">
                  <Mail size={32} className="contact-info-icon" />
                  <h3 className="heading-3">Email</h3>
                  <a href="mailto:jamesfarina18@gmail.com" className="body-medium contact-link">
                    jamesfarina18@gmail.com
                  </a>
                  <p className="caption">I'll respond within 24 hours</p>
                </div>

                <div className="contact-info-card">
                  <MapPin size={32} className="contact-info-icon" />
                  <h3 className="heading-3">Locations</h3>
                  <p className="body-medium">New Westminster</p>
                  <p className="body-medium">Burnaby</p>
                  <p className="caption">Lower Mainland, BC</p>
                </div>

                <div className="contact-info-card">
                  <Clock size={32} className="contact-info-icon" />
                  <h3 className="heading-3">Availability</h3>
                  <p className="body-medium">Monday - Friday: 6am - 8pm</p>
                  <p className="body-medium">Saturday: 8am - 6pm</p>
                  <p className="body-medium">Sunday: By appointment</p>
                </div>
              </div>

              <div className="contact-cta-box">
                <h3 className="heading-3">Why Choose Me?</h3>
                <ul className="contact-benefits-list">
                  <li className="body-small">Free initial consultation to discuss your goals</li>
                  <li className="body-small">Flexible scheduling for busy professionals</li>
                  <li className="body-small">Convenient locations in New Westminster & Burnaby</li>
                  <li className="body-small">Personalized programs tailored to your lifestyle</li>
                  <li className="body-small">Ongoing support beyond training sessions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick FAQ Link */}
      <section className="contact-faq-link-section">
        <div className="container">
          <div className="faq-link-box" data-aos="fade-up">
            <h2 className="heading-2">Have Questions?</h2>
            <p className="body-medium">
              Check out our comprehensive FAQ page for answers to common questions about training, 
              programs, pricing, and more.
            </p>
            <Link to="/faq">
              <Button className="btn-primary">
                View All FAQs
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
